/**
 * Batched reads of /publicProfiles for card enrichment (Browse, Dashboard).
 *
 * Replaces the previous one-getDoc-per-owner fan-out. On native the app forces
 * Firestore long-polling (src/firebase.js — WKWebView), so every read is a
 * discrete HTTP round trip; ~40 parallel getDocs per page load was the single
 * biggest round-trip cost on the two busiest pages. A documentId() `in` query
 * returns up to 30 profiles in ONE round trip. publicProfiles is public
 * (`allow read: if true` in firestore.rules:176), so the list query is
 * permitted for logged-in and logged-out visitors alike.
 *
 * Behavior contract (must match the fan-out it replaced):
 *  - An owner with no publicProfiles doc resolves to `fallback`
 *    (PROFILE_FALLBACK unless the caller passes its own — some legacy
 *    callers normalize raw data themselves and pass {}).
 *  - A failed chunk resolves every id in that chunk to PROFILE_FALLBACK
 *    (the old code fell back per-owner on getDoc error).
 *  - Ids present in `cache` are returned from cache and not re-fetched.
 */
import { collection, query, where, documentId, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

// Firestore's ceiling for `in` queries.
export const PROFILE_CHUNK_SIZE = 30;

export const PROFILE_FALLBACK = { username: 'User', completedTrades: 0 };

export function chunkIds(ids, size = PROFILE_CHUNK_SIZE) {
  const chunks = [];
  for (let i = 0; i < ids.length; i += size) {
    chunks.push(ids.slice(i, i + size));
  }
  return chunks;
}

/**
 * @param {string[]} ownerIds  distinct owner uids (order irrelevant)
 * @param {{cache?: Record<string, object>}} opts
 * @returns {Promise<Record<string, object>>} uid -> profile data (or fallback)
 */
export async function fetchPublicProfiles(ownerIds, { cache = {}, fallback = PROFILE_FALLBACK } = {}) {
  const result = {};
  const toFetch = [];
  for (const id of ownerIds) {
    if (!id) continue;
    if (cache[id]) {
      result[id] = cache[id];
    } else if (!toFetch.includes(id)) {
      toFetch.push(id);
    }
  }

  await Promise.all(
    chunkIds(toFetch).map(async (slice) => {
      try {
        const snap = await getDocs(
          query(collection(db, 'publicProfiles'), where(documentId(), 'in', slice))
        );
        snap.forEach((d) => {
          result[d.id] = d.data();
        });
      } catch (err) {
        console.error('publicProfiles batch fetch failed for chunk:', err);
      }
    })
  );

  for (const id of ownerIds) {
    if (id && !result[id]) result[id] = { ...fallback };
  }
  return result;
}
